// OCT-7341: TreatyApp: File Activity panel — add search, filters, and a count of matching activities - Frontend : ashish.atwal@resolutionlife.com
// The data-room Activity drawer. It loads recent activity once and then applies
// search + User/Event/Folder/File filtering entirely client-side over the loaded
// events. It owns the filter/search state, derives the dropdown options and
// search suggestions from the loaded events, shows a live count of matching
// activities, and renders the (pure) activity list with match highlighting.
import { FC, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Text,
} from '@chakra-ui/react';

import { useSplunkActivityLogger } from '@resolutionlife/ui';
import { useGetActivityFilterOptionsQuery } from '../../graphql/generated';
import {
  buildActivityFilterOptionsFromServer,
  buildSearchSuggestions,
  emptyActivityFilterState,
  isActivityFilterActive,
} from '../file-activity-list/activity-filtering';
import { ActivitySearch } from '../file-activity-list/activity-search';
import { clearCachedEvents } from '../file-activity-list/cache';
import { FileActivityListFilters } from '../file-activity-list/filters/filters';
import { MemoizedFileActivityList } from '../file-activity-list/list/list';
import { useFileActivityEvents } from '../file-activity-list/use-file-activity-events/use-file-activity-events';

export type ActivityFeedProps = {
  cedant: string;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

export const FileActivityPanel: FC<ActivityFeedProps> = ({
  isOpen,
  onClose,
  cedant,
}) => {
  const { cache } = useApolloClient();
  const activityLogger = useSplunkActivityLogger();
  const [filter, setFilter] = useState(emptyActivityFilterState);

  const {
    loading,
    error,
    cedantName,
    events,
    isFetchingMore,
    hasMore,
    fetchMore,
  } = useFileActivityEvents(cedant, {
    eventType: filter.eventType,
    user: filter.user,
    folder: filter.folder,
    file: filter.file,
    search: filter.search,
  });

  const { data: optionsData } = useGetActivityFilterOptionsQuery({
    variables: { cedant },
    fetchPolicy: 'no-cache',
    skip: !isOpen,
  });

  useEffect(() => {
    if (isOpen) {
      clearCachedEvents(cache, cedant);
      setFilter(emptyActivityFilterState);
      activityLogger.logActivityFeedOpen({ cedantId: cedant });
    }
  }, [cache, cedant, isOpen]);

  const loadedEvents = useMemo(() => events ?? [], [events]);
  const resolvedCedantName =
    optionsData?.cedant?.name ?? cedantName ?? cedant;

  const options = useMemo(
    () =>
      buildActivityFilterOptionsFromServer(
        optionsData?.cedant?.activityFilterOptions.users ?? [],
        optionsData?.cedant?.activityFilterOptions.paths ?? [],
        resolvedCedantName
      ),
    [optionsData, resolvedCedantName]
  );

  const filteredEvents = loadedEvents;

  const handleFilterChange = (next: typeof filter) => {
    // Log when the event-type facet changes, preserving existing Splunk behaviour.
    if (next.eventType && next.eventType !== filter.eventType) {
      activityLogger.logActivityFeedFilter({
        cedantId: cedant,
        eventType: next.eventType,
      });
    }
    setFilter(next);
  };

  const handleSearchChange = (term: string) => {
    setFilter((prev) => ({ ...prev, search: term }));
  };

  const handleClear = () => {
    setFilter(emptyActivityFilterState);
  };

  const hasOptions =
    options.users.length > 0 ||
    options.folders.length > 0 ||
    options.files.length > 0;
  const showControls =
    !error &&
    (loadedEvents.length > 0 || hasOptions || isActivityFilterActive(filter));

  return (
    <Drawer size="md" placement="right" isOpen={isOpen} onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton zIndex={1} data-testid="close-panel-btn" />
        <DrawerHeader>
          <Heading>Activity</Heading>
        </DrawerHeader>
        <DrawerBody as={Flex} flexDirection="column" gridGap="4">
          {showControls && (
            <>
              <ActivitySearch
                value={filter.search}
                onChange={handleSearchChange}
                buildSuggestions={(rawTerm) =>
                  buildSearchSuggestions(loadedEvents, resolvedCedantName, rawTerm)
                }
              />

              <FileActivityListFilters
                filter={filter}
                users={options.users}
                folders={options.folders}
                files={options.files}
                onChange={handleFilterChange}
                onClear={handleClear}
              />

              <Text
                fontSize="sm"
                color="gray.500"
                alignSelf="flex-end"
                data-testid="activity-count"
              >
                {filteredEvents.length}{' '}
                {filteredEvents.length === 1 ? 'activity' : 'activities'}
              </Text>
            </>
          )}

          <MemoizedFileActivityList
            cedant={cedant}
            cedantName={resolvedCedantName}
            onClose={onClose}
            events={filteredEvents}
            isFiltered={isActivityFilterActive(filter)}
            searchTerm={filter.search}
            loading={loading}
            error={error}
            isFetchingMore={isFetchingMore}
            hasMore={hasMore}
            fetchMore={fetchMore}
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
